// Page-specific quick-add row (mobile overhaul Phase 0 §0.4.8). A page calls
// `useQuickAddAction({...})` and its action becomes the FIRST row of the
// quick-add sheet while that page is mounted — Crew → "New crew", Clients →
// "New client", Finance → "New invoice", and so on.
//
// This module imports nothing from `pages/` or `Shell` on purpose: pages
// import it, Shell provides it, and neither direction may become a cycle.

import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';

export interface QuickAddAction {
  label: string;
  icon?: ReactNode;
  onSelect(): void;
}

/** A stable registration handle. `get()` reads the caller's latest action,
    so an inline `onSelect` closure never goes stale and re-registering on
    every render is unnecessary. */
interface QuickAddEntry {
  get(): QuickAddAction | null;
}

interface QuickAddContextValue {
  entry: QuickAddEntry | null;
  register(entry: QuickAddEntry): () => void;
}

const QuickAddContext = createContext<QuickAddContextValue>({
  entry: null,
  register: () => () => {},
});

export function QuickAddProvider({ children }: { children: ReactNode }) {
  const [entry, setEntry] = useState<QuickAddEntry | null>(null);
  const register = useCallback((next: QuickAddEntry) => {
    setEntry(next);
    // Only clear if we're still the registered page — a page that mounts
    // before the old one unmounts must not be wiped by the old cleanup.
    return () => setEntry((current) => (current === next ? null : current));
  }, []);
  // createElement, not JSX: this file stays `.ts` so pages can import the
  // hook without pulling a component module into their graph.
  return createElement(QuickAddContext.Provider, { value: { entry, register } }, children);
}

/** Register this page's quick-add row while the component is mounted.
    Pass `null` to register nothing (e.g. behind a loading guard). */
export function useQuickAddAction(action: QuickAddAction | null): void {
  const { register } = useContext(QuickAddContext);
  const latest = useRef<QuickAddAction | null>(action);
  latest.current = action;
  const active = action !== null;
  useEffect(() => {
    if (!active) return;
    return register({ get: () => latest.current });
  }, [active, register]);
}

/** The quick-add sheet's read side — the currently registered action, or null. */
export function useRegisteredQuickAddAction(): QuickAddAction | null {
  const { entry } = useContext(QuickAddContext);
  return entry ? entry.get() : null;
}
